import { useEffect, useState, type FormEvent } from "react";
import type { MetodoDistribucionGastos } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Cargando } from "../../components/Cargando";
import { Campo, Input, Select } from "../../components/Formulario";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

interface UnidadActiva {
  id: string;
  nombre: string;
  activo: boolean;
}

const TOLERANCIA = 0.01;

export function PanelDistribucionGastos({ puedeAdministrar }: { puedeAdministrar: boolean }) {
  const { mostrar } = useToast();
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [unidades, setUnidades] = useState<UnidadActiva[]>([]);
  const [metodo, setMetodo] = useState<MetodoDistribucionGastos>("partes_iguales");
  const [porcentajes, setPorcentajes] = useState<Record<string, string>>({});

  useEffect(() => {
    async function cargar() {
      setCargando(true);
      try {
        const [config, catalogo] = await Promise.all([
          api.get<{
            metodo: MetodoDistribucionGastos;
            porcentajes: { area_practica_id: string; porcentaje: number }[];
          }>("/distribucion-gastos"),
          api.get<{ items: UnidadActiva[] }>("/catalogos-estudio/areas-practica"),
        ]);
        setMetodo(config.metodo);
        const mapa: Record<string, string> = {};
        for (const p of config.porcentajes) mapa[p.area_practica_id] = String(p.porcentaje);
        setPorcentajes(mapa);
        setUnidades(catalogo.items.filter((u) => u.activo));
      } catch (err) {
        mostrar(err instanceof ErrorApi ? err.message : "No se pudo cargar la distribución de gastos.", "error");
      } finally {
        setCargando(false);
      }
    }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const suma = unidades.reduce((acc, u) => acc + (Number(porcentajes[u.id]) || 0), 0);
  const sumaValida = Math.abs(suma - 100) <= TOLERANCIA;

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      const cuerpo = {
        metodo,
        porcentajes:
          metodo === "porcentaje_fijo"
            ? Object.fromEntries(unidades.map((u) => [u.id, Number(porcentajes[u.id]) || 0]))
            : {},
      };
      await api.put("/distribucion-gastos", cuerpo);
      mostrar("Distribución guardada.", "exito");
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar.", "error");
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) return <Cargando />;

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-texto">Distribución de gastos generales</h3>
        <p className="text-xs text-texto-secundario">
          Cómo se reparte cada Gasto General (sin Unidad) entre las Unidades de Negocio activas, para calcular la
          rentabilidad de cada una.
        </p>
      </div>

      {unidades.length === 0 ? (
        <p className="text-xs text-texto-secundario">
          Creá al menos una Unidad de Negocio activa para configurar el reparto.
        </p>
      ) : (
        <form onSubmit={guardar} className="space-y-4">
          <Campo etiqueta="Método">
            <Select
              value={metodo}
              onChange={(e) => setMetodo(e.target.value as MetodoDistribucionGastos)}
              disabled={!puedeAdministrar}
            >
              <option value="partes_iguales">Partes iguales</option>
              <option value="porcentaje_fijo">Porcentaje fijo</option>
            </Select>
          </Campo>

          {metodo === "porcentaje_fijo" && (
            <div className="space-y-2">
              {unidades.map((u) => (
                <Campo key={u.id} etiqueta={u.nombre}>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={porcentajes[u.id] ?? ""}
                    onChange={(e) => setPorcentajes((p) => ({ ...p, [u.id]: e.target.value }))}
                    disabled={!puedeAdministrar}
                  />
                </Campo>
              ))}
              <p className={`text-xs font-medium ${sumaValida ? "text-exito" : "text-error"}`}>
                Total: {suma.toFixed(2)}%{!sumaValida && " — debe sumar 100%"}
              </p>
            </div>
          )}

          {puedeAdministrar && (
            <Boton type="submit" cargando={guardando} disabled={metodo === "porcentaje_fijo" && !sumaValida}>
              Guardar
            </Boton>
          )}
        </form>
      )}
    </div>
  );
}
